import { ruleFacts, ruleSentenceBase } from "./ruleSentence";
import { shortDate } from "./weekLine";
/* THE WORKOUT BUILDER'S HEAD AND FOOT — W72 step 3 (Proposal 3). Pure, so every sentence the builder prints about
 * itself is driven by a test rather than assembled inside the component. */


/** Edits closer together than this are ONE undo step — ⌘Z walks back a whole thought, not a keystroke. */
export const UNDO_BURST_MS = 800;
/** How far back ⌘Z reaches. */
export const UNDO_DEPTH = 100;

/**
 * THE WORKOUT'S HISTORY. `past` is what Undo walks back to; `future` (🔴 W85, his report 2026-09-27: *"instead of cmd and z we
 * can use 2 buttons where the text there is, like we do on the app. the undo and redo buttons"*) is what Redo walks forward
 * to — filled only by an undo, and EMPTIED by any new edit (redoing onto a draft that has since changed would replay an
 * edit onto a workout it was never made on). `future` is optional so a history written before it existed still reads.
 */
export type History<T> = { past: T[]; future?: T[]; lastAt: number };

/** Record the draft BEFORE an edit, unless it is part of the same burst as the last one. Any edit clears the redo stack. */
export function pushHistory<T>(h: History<T>, before: T | null, now: number, burstMs: number): History<T> {
  if (before === null) return h;
  if (h.past.length > 0 && now - h.lastAt < burstMs) return { past: h.past, future: [], lastAt: now };
  const past = [...h.past, before];
  return { past: past.length > UNDO_DEPTH ? past.slice(past.length - UNDO_DEPTH) : past, future: [], lastAt: now };
}

/** Undo: the draft on screen goes onto `future`, the last of `past` comes back. `null` when there is nothing to undo.
 *  `lastAt` resets, so the next edit after an undo starts a new step rather than joining a burst it was never part of. */
export function undoStep<T>(h: History<T>, current: T): { history: History<T>; draft: T } | null {
  if (h.past.length === 0) return null;
  const draft = h.past[h.past.length - 1] as T;
  return { history: { past: h.past.slice(0, -1), future: [...(h.future ?? []), current], lastAt: 0 }, draft };
}

/** Redo: the mirror of `undoStep`. `null` when nothing was undone since the last edit. */
export function redoStep<T>(h: History<T>, current: T): { history: History<T>; draft: T } | null {
  const future = h.future ?? [];
  if (future.length === 0) return null;
  const draft = future[future.length - 1] as T;
  return { history: { past: [...h.past, current], future: future.slice(0, -1), lastAt: 0 }, draft };
}

/** The workout's one line under its description: `18 exercises · 7 without a rule` — counts, never a warning. */
export function workoutSummary(exercises: number, rules: { missing: number; counted: number }): string | null {
  // An empty workout says nothing here — absence is said by absence (F11 lesson 27's rule); the empty drop zone speaks.
  if (exercises === 0) return null;
  const n = `${exercises} ${exercises === 1 ? "exercise" : "exercises"}`;
  if (rules.counted === 0 || rules.missing === 0) return n;
  return `${n} · ${rules.missing} without a rule`;
}

/**
 * 🔴 W88 — THE HEADER'S ONE CAPTION (his yes, 2026-09-28): `6 exercises · Barbell, Bench, Kettlebell, Squat Rack`. It
 * replaces the count line AND the `EQUIPMENT` heading with its chips — three blocks saying two facts. The equipment is the
 * derived list the chips drew (never stored); a workout whose every exercise is bodyweight says so; one whose rows could
 * not all be read says the list may be short rather than printing a short list as a complete one.
 * ⚠️ THE MOCK'S `about 55 min` IS NOT HERE: nothing on the web derives a duration from a prescription, and a made-up
 * figure on the coach's screen is a claim nobody measured. It joins this line the day a derived estimate exists.
 */
export function workoutCaption(exercises: number, rules: { missing: number; counted: number },
                               equipment: { labels: readonly string[]; unknown: number }): string | null {
  const head = workoutSummary(exercises, rules);
  if (head === null) return null;
  const kit = equipment.labels.length > 0
    ? equipment.labels.join(", ")
    : equipment.unknown === 0 ? "No equipment" : null;
  const short = equipment.unknown > 0
    ? `equipment for ${equipment.unknown} ${equipment.unknown === 1 ? "exercise" : "exercises"} not read`
    : null;
  return [head, kit, short].filter((x): x is string => x !== null).join(" · ");
}

/**
 * The foot's second sentence, MEASURED rather than quoted: a template edit reaches only the days assigned from it
 * AFTER the edit. The next-week writer clones LAST WEEK's copy, never the template (`CoachNextWeekService` §1),
 * and copy-on-assign never reaches a day already given (his 2026-08-27 sentence). `null` = not counted (a new
 * workout, or the count failed) — never printed as zero.
 */
/** 2026-10-07 — the builder opened from a review's `Edit next week` holds ONE client's assigned COPY, not a template:
 *  `usedOnLine`'s "Not assigned yet" would be false there. What its save reaches, said instead. */
export const NEXT_WEEK_COPY_LINE = "This client's next week only · the Library workout stays";
/** William's item 4 — the foot over a client's day: the edit is theirs alone, and the library is not touched. */
export const CLIENT_DAY_COPY_LINE = "This client's day only · your Library stays as it is";
/** William's item 4 — the head's eyebrow over a client's day: who and when (`Alex · Wed 14 Oct`); the name alone while the
 *  day is not known yet, never an empty `·`. */
export function clientDayEyebrow(clientName: string, day: string | null): string {
  const who = clientName.trim() === "" ? "Client" : clientName.trim();
  return day === null ? who : `${who} · ${shortDate(day)}`;
}
export function usedOnLine(copies: number | null): string {
  if (copies === null) return "Edits reach days assigned after them";
  if (copies === 0) return "Not assigned yet · edits reach days assigned after them";
  return `Used on ${copies} assigned ${copies === 1 ? "day" : "days"} · edits reach only days assigned after them`;
}

/** W73 (W72 step 5) — the rule a row carries, as the SENTENCE its numbers make (`ruleSentence.ts`, the Swift twin held
 *  equal to a corpus the Swift emits): `Rule · +1 rep on one set each clean week to 10 on every set, then +2.5 kg and
 *  back to 6,6,6`. It replaced step 3's kind NAME, whose comment said a sentence "would be a claim about the progression
 *  engine this screen cannot rule" — the corpus is what lets this screen rule it: every clause is EXECUTED against the
 *  rule in gate `w72-5-the-library`. `lacks` is what the config never stated, drawn in the accent (CLAUDE.md: the
 *  missing parameters announce themselves). `null` when there is no rule (the header counts those). */
export function ruleTag(config: unknown): { text: string; lacks: string | null } | null {
  const facts = ruleFacts(config);
  if (facts === null) return null;
  return {
    text: `Rule · ${ruleSentenceBase(facts.kind)}`,
    lacks: facts.lacks.length === 0 ? null : facts.lacks.map((l) => l.words).join("; "),
  };
}

/**
 * 🔴 W85 — THE TITLE'S AUTO-GROW HEIGHT, OR NONE. His report, 2026-09-27: *"why the title thing when empty looks like that and
 * only when i type looks ok?"* MEASURED 2026-09-27, in Chromium AND in WebKit (the iOS Simulator's Safari, his browser's
 * engine): on a NEW workout the grow effect ran before `showModal()`, while the window was still `display: none`, so the
 * field's `scrollHeight` read 0 and the effect wrote `height: 0px` — a 3 px box (its border) with the placeholder's glyphs
 * cut into short dashes and the focus ring drawn round the sliver. An existing workout loads AFTER the window opens, and
 * any keystroke re-measures, which is why typing "fixed" it. So a height of 0 is not a height: it means "not laid out",
 * and nothing is written (the field keeps its natural one-line height until the window opens and re-measures).
 */
export function titleHeight(scrollHeight: number): string | null {
  return scrollHeight > 0 ? `${String(scrollHeight)}px` : null;
}
